import { FC, useContext, useEffect, useState } from "react";
import { useRouter } from "next/router";
import { Loader } from "@moneyforward/mfx_biz_react_components";
import { useIsSpMode } from "@/hooks";
import { AuthContext } from "@/contexts/AuthContext";
import { useGetCompanyInfoLazyQuery, Company } from "@/generated/graphql";
import paths from "@/libs/paths";
import useRollbarUtil from "@/components/hooks/useRollbarUtil";
import { MainTemplate } from "../templates";
import config from "@/libs/config";
import { PlanCancelPreprocessForm } from "@/components/organisms/PlanCancelPreprocessForm";

type Props = {};

export const PlanCancelPreprocessPage: FC<Props> = (props) => {
  // --------------------------------------------------
  // Top level definitions
  // --------------------------------------------------
  const { getNotifyErrorHandlerForGraphQL } = useRollbarUtil();
  const { userInfo } = useContext(AuthContext);
  const userSub = userInfo.sub;
  const router = useRouter();
  const [companyInfo, setCompanyInfo] = useState<Company | null>(null); // Company Info from ID Server

  // --------------------------------------------------
  // Data Fetching Process
  // --------------------------------------------------
  const [getCompanyInfoQuery, { loading: getCompanyInfoLoading }] =
    useGetCompanyInfoLazyQuery({
      onCompleted: (data) => {
        if (data?.company) {
          setCompanyInfo(data?.company as Company);
        }
      },
      onError: getNotifyErrorHandlerForGraphQL(
        "Failed to get company info - PlanCancelPreprocessPage"
      ),
    });

  // --------------------------------------------------
  // Side Effects
  // --------------------------------------------------
  useEffect(() => {
    getCompanyInfoQuery();
  }, [getCompanyInfoQuery]);

  // --------------------------------------------------
  // Handlers
  // --------------------------------------------------
  const handleClickProceed = () => {
    if (companyInfo !== null && companyInfo?.displayCompanyId) {
      // setup base url and params
      const gformInfo = config.CollectCancelReasonGFormInfo;

      // fail safe if we don't have the required info
      if (!gformInfo) {
        // skip the questionnaire and redirect to the plan-cancel page directly
        router.push(paths.PlanCancel);
        return;
      }

      // get the required info
      const { PrefilledLink, DisplayIdParam } = gformInfo;
      const url = new URL(PrefilledLink);
      const dispCompanyId = companyInfo.displayCompanyId;

      // append params
      url.searchParams.append(DisplayIdParam, dispCompanyId);

      // redirect to the questionnaire page
      router.push(url.toString());
    }
  };

  // --------------------------------------------------
  // Conditional Rendering
  // --------------------------------------------------
  if (getCompanyInfoLoading) {
    return (
      <MainTemplate>
        <Loader>Loading ...</Loader>
      </MainTemplate>
    );
  }

  return (
    <MainTemplate>
      <div css={{ width: "100%", display: "flex", justifyContent: "center" }}>
        <PlanCancelPreprocessForm
          onClickProceed={handleClickProceed}
          onClickCancel={() => {
            router.replace(paths.ContractManagement);
          }}
        />
      </div>
    </MainTemplate>
  );
};
